import React, { useState, useRef } from "react";
import classnames from "classnames";
import CopyToClipboard from "react-copy-to-clipboard";
import HeaderIconSvg from "@ecologi/assets/icons/header-icon.svg";
import TickIcon from "@ecologi/assets/icons/tick.svg";
import { Modal, ModalContent, ModalHeaderSection } from "../DeprecatedModal";
import { Button } from "../Button";
import styles from "./ReferralModal.module.scss";
import { NUMBER_OF_REFERRAL_TREES } from "@ecologi/shared";

const Number = ({
  children,
  withLine,
}: {
  children: string;
  withLine?: boolean;
}) => {
  return (
    <div className={classnames(styles.number)}>
      {withLine && <div className={styles.stepperDottedLine} />}
      <span className={styles.stepperText}>{children}</span>
    </div>
  );
};

const NumberContainer = ({
  number,
  withLine,
  children,
}: {
  number: string;
  children: React.ReactNode;
  withLine?: boolean;
}) => {
  return (
    <div className={styles.stepperSection}>
      <div className={styles.numberContainer}>
        <Number withLine={withLine}>{number}</Number>
      </div>
      {children}
    </div>
  );
};

export enum ReferralModalVariants {
  B2B = "B2B",
  CONSUMER = "CONSUMER",
}

const ReferralModalVariantContentMap = {
  [ReferralModalVariants.B2B]: {
    noun: "network",
    pronoun: "anyone signs up,",
    numTrees: NUMBER_OF_REFERRAL_TREES,
  },
  [ReferralModalVariants.CONSUMER]: {
    noun: "friends",
    pronoun: "they sign up,",
    numTrees: NUMBER_OF_REFERRAL_TREES,
  },
};

type Props = {
  onClose: () => void;
  isOpen: boolean;
  referralLink: string;
  variant: ReferralModalVariants;
};

export const ReferralModal = ({
  referralLink,
  variant = ReferralModalVariants.CONSUMER,
  ...props
}: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const referralButtonRef = useRef<HTMLButtonElement>(null);

  const { noun, pronoun, numTrees } = ReferralModalVariantContentMap[variant];

  return (
    <Modal {...props} id="tour-referral-banner">
      <ModalHeaderSection variant="dark">
        <ModalContent centered>
          <HeaderIconSvg className={styles.headerIcon} />

          <div className={styles.titleSection}>
            <h2 className={styles.title}>Invite your {noun}</h2>
          </div>

          <p className={styles.text}>
            Share this special link with your {noun} and if {pronoun} you’ll
            both receive {numTrees} sparkly trees!
          </p>
        </ModalContent>
        <div className={styles.referralLinkSection}>
          <CopyToClipboard text={referralLink} onCopy={() => setIsCopied(true)}>
            <div className={styles.referralLinkContainer}>
              <p className={styles.referralLink}>{referralLink}</p>
            </div>
          </CopyToClipboard>

          <CopyToClipboard
            text={referralLink}
            onCopy={() => {
              setIsCopied(true);

              if (referralButtonRef && referralButtonRef.current) {
                referralButtonRef.current.blur();
              }
            }}
          >
            <span className={styles.buttonRow}>
              <div className={styles.buttonContainer}>
                <Button
                  ref={referralButtonRef}
                  primary
                  icon={isCopied ? TickIcon : undefined}
                  text={isCopied ? "Copied" : "Copy special link"}
                />
              </div>
            </span>
          </CopyToClipboard>
        </div>
      </ModalHeaderSection>

      <div className={styles.stepperRoot}>
        <NumberContainer number="1" withLine>
          <p className={styles.text}>Send {noun} your special link</p>
        </NumberContainer>

        <NumberContainer number="2">
          <p className={styles.text}>They click the link and sign up</p>
        </NumberContainer>

        <NumberContainer number="3">
          <p className={styles.text}>You both get {numTrees} trees! ✨</p>
        </NumberContainer>
      </div>
    </Modal>
  );
};
